import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
} from "react";
import Grid, { SelectColumn } from "./react-data-grid";
import DataTablePagination from "Components/DataTablePagination/DataTablePagination";
import TableLoader from "./TableLoader";
import { FeatureSafe } from "Common/ErrorBoundaries";
import isNull from "Utils/isNull";
import "./styles.css";
import HoverRevealScrollbarWrapper from "Components/HoverRevealScrollbarWrapper/HoverRevealScrollbarWrapper";
const formattedColumns = (columns, selectColumn) => {
  let updatedColumns = columns?.map((column) => {
    const renderWithTooltip = ({ row }) => (
      <span title={row[column.key]}>{row[column.key]}</span>
    );

    const hasCustomRender = column.renderCell || column.formatter;

    const context = document.createElement("canvas").getContext("2d");
    context.font = "12.35px Open Sans, sans-serif"; // Make sure this matches actual table font
    const textWidth = context.measureText(column.name).width;
    const padding = 25;
    try {
      // If minWidth is explicitly set, preserve it and keep width if provided (for resizable columns with starting width)
      // Otherwise, convert width to minWidth for auto-sizing behavior
      const hasExplicitMinWidth = column.minWidth !== undefined;

      if (!hasExplicitMinWidth) {
        column.minWidth = column.width ?? textWidth + padding;
      }

      if (column.maxWidth) {
        column.width = column.maxWidth;
        // delete column["maxWidth"];
      } else if (column.width && !hasExplicitMinWidth) {
        // Only delete width if minWidth wasn't explicitly set (preserve width for resizable columns with starting width)
        delete column.width;
      }
    } catch (error) {
      console.log(error, "GRID-RESIZING");
    }

    return {
      ...column,

      renderCell: hasCustomRender
        ? column.formatter || column.renderCell
        : renderWithTooltip,
    };
  });

  if (selectColumn) updatedColumns.unshift(SelectColumn);

  return updatedColumns;
};
const getContentBasedGridHeight = ({
  rowCount,
  rowHeight = 40,
  headerRowHeight = 41,
  hasSummaryRows = false,
  summaryRowsHeight = 40,
  minHeight,
  maxHeight,
  contentBasedMaxHeight,
}) => {
  const headerHeight = Number(headerRowHeight) || 41;
  const rowHeightPx = Number(rowHeight) || 40;
  const count = Math.max(0, Number(rowCount) || 0);
  const dataHeight = count * rowHeightPx;
  const summaryHeight = hasSummaryRows ? Number(summaryRowsHeight) || 40 : 0;
  let height = headerHeight + dataHeight + summaryHeight;

  const min = Number(minHeight);
  const max = Number(maxHeight);
  if (!Number.isNaN(min) && height < min) {
    height = min;
  }
  if (!Number.isNaN(max) && height > max) {
    height = max;
  }
  if (height > contentBasedMaxHeight) {
    height = contentBasedMaxHeight;
  }
  return height;
};

export const DataGrid = React.forwardRef((props, ref) => (
  <FeatureSafe componentName="DataGrid">
    <DataGridMain
      {...props}
      columns={formattedColumns(props.columns, props.selectColumn)}
    />
  </FeatureSafe>
));

const DataGridMain = React.forwardRef((props) => {
  const [loadingMoreRows, setLoadingMoreRows] = useState(false);
  const [sortColumns, setSortColumns] = useState([]); // columnKey,direction
  const [noRows, setNoRows] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [previousRows, setPreviousRows] = useState([]);
  const [tableColumns, setTableColumns] = useState(props.columns);
  const [rows, setRows] = useState(props.rows);
  const { onRowClick, rowHeight, onSelectedRowsChange, selectedRows } = props;
  const [gridHeight, setGridHeight] = useState({
    formHeight: "",
    gridHeight: "",
  });
  const formRef = useRef();
  const gridHandleRef = useRef(null);

  useLayoutEffect(() => {
    if (!formRef.current && props.formId) {
      formRef.current = document.querySelector(`#${props.formId}`);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { height } = entry.contentRect;
        setGridHeight((prev) => ({ ...prev, formHeight: height }));
      }
    });

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    // Cleanup on unmount
    return () => {
      observer.disconnect();
    };
  }, [formRef.current]);

  //////////////
  const onSortColumnsChange = (sortColumns) => {
    setSortColumns(sortColumns.slice(-1));
  };

  useEffect(() => {
    setRows(props.rows?.slice(0));
    setTableColumns(props.columns);
  }, [props.columns, props.rows]);

  const rowKeyGetter = (row) => {
    return row.id;
  };
  const addRowIndex = (rows) => {
    let Rows = rows.map((row, index) => {
      row = {
        ...row,
        rowIndex: index,
      };
      return row;
    });
    return Rows;
  };
  const checkFieldType = (columnCellValue) => {
    var date = Date.parse(columnCellValue);
    if (isNaN(date)) return false;
    else return true;
  };
  const sortedRows = useMemo(() => {
    setLoadingMoreRows(false);
    if (sortColumns.length === 0) {
      if (props.onScrollPagination) {
        return [...previousRows, ...addRowIndex(rows)];
      } else {
        return addRowIndex(rows);
      }
    }
    let previousIndex = "";
    let nextIndex = "";
    const comparer = (a, b) => {
      let isDateColumn = false;
      //////// For Link Fields ////////
      if (
        !isNull(b[sortColumns[0].columnKey]) &&
        !isNull(b[sortColumns[0].columnKey].props)
      ) {
        isDateColumn = checkFieldType(b[sortColumns[0].columnKey].props.title);
        if (isDateColumn === true) {
          previousIndex = new Date(a[sortColumns[0].columnKey].props.title);
          nextIndex = new Date(b[sortColumns[0].columnKey].props.title);
        } else {
          previousIndex = isNull(a[sortColumns[0].columnKey].props.title)
            ? ""
            : a[sortColumns[0].columnKey].props.title;
          nextIndex = isNull(b[sortColumns[0].columnKey].props.title)
            ? ""
            : b[sortColumns[0].columnKey].props.title;
        }
        if (sortColumns[0].direction === "ASC") {
          return previousIndex > nextIndex ? 1 : -1;
        } else if (sortColumns[0].direction === "DESC") {
          return previousIndex < nextIndex ? 1 : -1;
        }
      }
      //////// For simple Fields////////
      else {
        isDateColumn = checkFieldType(b[sortColumns[0].columnKey]);
        if (isDateColumn === true) {
          previousIndex = new Date(a[sortColumns[0].columnKey]);
          nextIndex = new Date(b[sortColumns[0].columnKey]);
        } else {
          previousIndex = isNull(a[sortColumns[0].columnKey])
            ? ""
            : a[sortColumns[0].columnKey];
          nextIndex = isNull(b[sortColumns[0].columnKey])
            ? ""
            : b[sortColumns[0].columnKey];
        }
        if (sortColumns[0].direction === "ASC") {
          return previousIndex > nextIndex ? 1 : -1;
        } else if (sortColumns[0].direction === "DESC") {
          return previousIndex < nextIndex ? 1 : -1;
        }
      }
    };
    let filterRows = props.onScrollPagination
      ? [...previousRows, ...props.rows]
      : rows;
    const sortRows =
      sortColumns.direction === "NONE" ? filterRows : filterRows.sort(comparer);
    return addRowIndex(sortRows);
    //setsortRows(sortRows)
  }, [rows, sortColumns]);

  const isAtBottom = ({ currentTarget }) => {
    return (
      currentTarget.scrollTop + 10 >=
      currentTarget.scrollHeight - currentTarget.clientHeight
    );
  };
  const handleScroll = async (event) => {
    let newRows = [...previousRows, ...props.rows];
    if (rows.length !== 0) {
      if (loadingMoreRows || !isAtBottom(event)) {
        setNoRows(false);
      } else if (newRows.length !== props.totalCount) {
        setLoadingMoreRows(true);
        // let getRowsLength = rows.length + props.pageSize
        setPreviousRows(newRows);
        setPageNo(pageNo + 1);
        await props.getMoreRows(pageNo + 1);
      } else {
        setLoadingMoreRows(false);
        setNoRows(true);
      }
    }
  };

  // const rowRenderer = (key, rowRenderProps) => {
  //   function onRowReorder(fromIndex, toIndex) {
  //     const newRows = [...rows];
  //     newRows.splice(toIndex, 0, newRows.splice(fromIndex, 1)[0]);
  //     setRows(newRows);
  //     if (props.setUpdatedList) {
  //       props.setUpdatedList(newRows);
  //     }
  //   }
  //   return (
  //     <DraggableRowRenderer
  //       key={key}
  //       {...rowRenderProps}
  //       onRowReorder={onRowReorder}
  //     />
  //   );
  // };
  const summaryRows = rows.length !== 0 ? [rows] : null;
  const contentBasedHeight = props.contentBasedHeight
    ? getContentBasedGridHeight({
        rowCount: Array.isArray(sortedRows) ? sortedRows.length : 0,
        rowHeight: rowHeight ?? 40,
        headerRowHeight: props.headerRowHeight ?? 41,
        hasSummaryRows: Boolean(props.fixedFooter && props.summaryRows?.length),
        summaryRowsHeight: props.summaryRowsHeight ?? 40,
        minHeight: props.contentBasedMinHeight,
        maxHeight: props.contentBasedMaxHeight,
        contentBasedMaxHeight: props.contentBasedMaxHeight,
      })
    : null;

  useLayoutEffect(() => {
    const root = gridHandleRef.current?.element;
    if (!root || !(root instanceof HTMLElement)) return;
    root.setAttribute("data-hover-scrollable", "true");
    return () => root.removeAttribute("data-hover-scrollable");
  }, [
    sortedRows,
    tableColumns,
    contentBasedHeight,
    gridHeight.formHeight,
    props.gridHeightOffset,
    props.contentBasedHeight,
    props.fixedFooter,
    props.summaryRows,
  ]);

  return (
    <div
      className="relative rounded-md border border-gray-200 overflow-hidden"
      style={{
        width: "100%",
        // minHeight: `calc(100vh - ${
        //   gridHeight.formHeight + (props.gridHeightOffset ?? 220) + "px"
        // })`,
        // height: `calc(100vh - ${
        //   gridHeight.formHeight + (props.gridHeightOffset ?? 220) + "px"
        // })`,
        // maxHeight: `calc(100vh - ${
        //   gridHeight.formHeight + (props.gridHeightOffset ?? 220) + "px"
        // })`,
        // transition: "height 0.1s ease",
      }}
    >
      {/* <span className="rdg_graySpace"></span> */}
      <HoverRevealScrollbarWrapper>
        <Grid
          ref={gridHandleRef}
          rowClass={props.rowClass}
          rowKeyGetter={rowKeyGetter} // is required to get row key for selectcolumn
          summaryRows={props.fixedFooter ? props.summaryRows : null} // for add footer in grid // old version
          bottomSummaryRows={props.fixedFooter ? props.summaryRows : null} // for add footer in grid
          columns={tableColumns}
          rows={sortedRows ?? []}
          sortColumns={sortColumns}
          onSortColumnsChange={onSortColumnsChange} // set column and  direction to sortColumns
          // onRowClick={onRowClick}
          onCellClick={(_) => (onRowClick ? onRowClick(_.row) : () => {})}
          selectedRows={selectedRows}
          // className="min-h-fit"

          style={{
            fontSize: ".95rem",
            transition: "height 0.1s ease",
            // flex: "1",
            minHeight:
              Array.isArray(sortedRows) && sortedRows.length === 0
                ? "130px"
                : "auto",
            height:
              contentBasedHeight != null
                ? `${contentBasedHeight + 10}px`
                : `calc(100vh - ${
                    gridHeight.formHeight +
                    (props.gridHeightOffset ?? 270) +
                    "px"
                  })`,

            width: "100%",
            // width: "1",
            // width: props.width ? props.width : "100%",
            // maxHeight: `calc(100vh - ${props.maxHeight ?? "300px"})`,
            // minHeight: `calc(100vh - ${props.minHeight ?? "200px"})`,
            // overflowY:
            //   !props.onScrollPagination && rows.length <= 10 ? "hidden" : "",
            userSelect: "text",
          }}
          // className="flex-1 overflow-y-auto"
          onSelectedRowsChange={onSelectedRowsChange}
          isRowSelectionDisabled={props.isRowSelectionDisabled}
          onScroll={props.onScrollPagination ? handleScroll : null}
          rowHeight={rowHeight ? rowHeight : 40}
          // className={
          //   props.dynamicClass
          //     ? props.dynamicClass
          //     : `${rows.length === 0 ? "overflow-hidden" : "overflow-y-auto"}`
          // }
          // rowClass={
          //   props.rowClass
          //     ? props.rowClass
          //     : (e) => {
          //         return "gridPadding";
          //       }
          // }

          onRowsChange={props.onRowsChange ? props.onRowsChange : undefined}
          headerRowHeight={props.headerRowHeight ? props.headerRowHeight : 41}
          // components={
          //   props.isDraggableRows
          //     ? { rowRenderer: (r) => rowRenderer(r.rowIdx, r) }
          //     : null
          // }
          // onColumnResize={() => {}}

          defaultColumnOptions={{
            // draggable: true,
            // minWidth: 130,
            resizable: true,
            sortable: true,
          }}
          renderers={{
            renderCheckbox: ({ checked, onChange, disabled }) => {
              return (
                <input
                  className="rdg-checkbox"
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  style={
                    disabled
                      ? { opacity: 0.45, cursor: "not-allowed" }
                      : undefined
                  }
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.preventDefault();
                    onChange(e.target.checked);
                  }}
                />
              );
            },
          }}
        />
      </HoverRevealScrollbarWrapper>

      {loadingMoreRows && (
        <div
          style={{
            width: " 180px",
            padding: "8px 16px",
            position: "absolute",
            bottom: "8px",
            right: "8px",
            color: "white",
            lineHeight: "35px",
            background: "rgb(0 0 0 / 0.6)",
          }}
        >
          Loading More Rows...
        </div>
      )}
      {noRows && (
        <div
          style={{
            width: " 180px",
            padding: "8px 16px",
            position: "absolute",
            bottom: "8px",
            right: "8px",
            color: "white",
            lineHeight: "35px",
            background: "rgb(0 0 0 / 0.6)",
          }}
        >
          No more rows
        </div>
      )}
      {props.isLoading || rows.length === 0 ? (
        <div
          className="my-auto"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <TableLoader
            isLoading={loadingMoreRows ? false : props.isLoading}
            dataLength={rows.length}
            customMessage={
              props.customMessage ? props.customMessage : undefined
            }
          />
        </div>
      ) : null}

      {props.manualPagination && props.rows.length > 0 ? (
        <DataTablePagination
          handlePerRowsChange={props.handlePerRowsChange}
          handlePageChange={props.handlePageChange}
          pagination={props.pagination}
          loading={props.isLoading}
        />
      ) : null}
    </div>
  );
});
